<template>
  <div
    ref="container"
    :class="$style.container"
  >
    <table
      v-show="tableData.length"
      :class="$style.table"
    >
      <TableHeader
        :columns="columns"
        :renderActionColumn="renderActionColumn"
        :sortBy="sortBy"
        @headerClick="column => $emit('headerClick', column)"
      />
      <TableBody
        :columns="columns"
        :inContainer="inContainer"
        :isRefreshing="isRefreshing"
        :renderActionColumn="renderActionColumn"
        :tableData="tableData"
        @cellClick="emitCellClick"
      >
        <template
          v-slot:expandedContent="props"
        >
          <slot
            :columns="props.columns"
            name="expandedContent"
            :record="props.record"
          />
        </template>
      </TableBody>
    </table>
    <div
      v-show="tableData.length && moreToLoad"
      ref="loadMoreText"
      :class="$style.loadMoreText"
    >
      <span>Loading more items...</span>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" v-show="loadingAdditionalData" :class="$style.loadingIcon" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill="currentColor" d="M27.662 6.749a3.242 3.242 0 11-6.336 1.38 3.242 3.242 0 016.336-1.38zm-7.974-2.806a2.976 2.976 0 11-5.816 1.268 2.976 2.976 0 015.816-1.268zm-7.81 2.435a2.71 2.71 0 11-5.296 1.156 2.71 2.71 0 015.296-1.156zm-4.661 6.484a2.444 2.444 0 11-4.778 1.042 2.444 2.444 0 014.779-1.042zm-.115 7.566a2.179 2.179 0 11-4.258.928 2.179 2.179 0 014.258-.928zm3.06 4.927a1.913 1.913 0 11-3.739.815 1.913 1.913 0 013.739-.815zm5.634 3.046a1.648 1.648 0 11-3.22.702 1.648 1.648 0 013.22-.702zm6.654-.596a1.382 1.382 0 11-2.7.59 1.382 1.382 0 012.7-.59zm5.515-4.633a1.116 1.116 0 11-2.182.474 1.116 1.116 0 012.182-.474zm1.632-7.24a.85.85 0 11-1.662.362.85.85 0 011.662-.362z"/></svg>
    </div>
    <LoadingOverlay
      :showLoadingOverlay="isRefreshing"
      :zIndex="540"
    />
    <NoRecordsFound
      v-show="!tableData.length && !isRefreshing"
      :class="$style.noResultsText"
    >
      <template v-slot:emptyMessage>
        <slot name="emptyMessage" />
      </template>
    </NoRecordsFound>
  </div>
</template>

<script>
import LoadingOverlay from 'components/LoadingOverlay/LoadingOverlay.vue';
import NoRecordsFound from 'components/Tables/NoRecordsFound.vue';
import TableBody from 'components/Tables/TableBody.vue';
import TableHeader from 'components/Tables/TableHeader.vue';
import TableMixin from 'components/Tables/TableMixin';

export default {
  name: 'TableWithEndlessScroll',

  components: {
    LoadingOverlay,
    NoRecordsFound,
    TableBody,
    TableHeader,
  },

  mixins: [
    TableMixin,
  ],

  props: {
    emptyText: {
      type: String,
      default: '',
    },
    isRefreshing: {
      type: Boolean,
      default: false,
    },
    loadingAdditionalData: {
      type: Boolean,
      default: false,
    },
    moreToLoad: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      expandedIds: [],
    };
  },

  watch: {
    tableData: {
      deep: true,
      handler() {
        // When new data has been loaded, restart the "load more" listener
        this.stopListeningToLoadMore();
        this.listenToLoadMore();
      },
    },
  },

  methods: {
    emitCellClick(column, value) {
      this.$emit('cellClick', column, value);
    },

    emitLoadMore([entry]) {
      if (!entry.isIntersecting) return;

      // Stop listening so callback doesn't fire again when "load more" text goes out of sight
      this.stopListeningToLoadMore();
      this.$emit('loadMore');
    },

    listenToLoadMore() {
      if (!this.observer) {
        this.observer = new IntersectionObserver(this.emitLoadMore, {
          root: this.$refs.container,
          threshold: 0.4,
        });
      }
      this.observer.observe(this.$refs.loadMoreText);
    },

    stopListeningToLoadMore() {
      if (this.observer) this.observer.unobserve(this.$refs.loadMoreText);
    },
  },
};
</script>

<style scoped module>
@import './css/tableStyles.css';

@value (gradientLight6) from '~css/colors.css';

.table tbody tr:last-child .tableCell { border-bottom: none; }

.container { overflow: auto; }

.loadMoreText {
  border-top: 1px solid gradientLight6;
  font-size: 13px;
  font-style: italic;
  padding: 16px;
  text-align: center;
}

.loadMoreText > * { vertical-align: middle; }

.loadingIcon {
  animation: spin 2s infinite linear;
  fill: var(--jcPrimary);
  height: 16px;
  margin-left: 5px;
  position: relative;
  top: -1px;
  width: 16px;
}

.noResultsText {
  padding: 50px;
  width: 450px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
